import app from 'flarum/forum/app';

// Mithril is global in Flarum.

/**
 * Widget-zone plumbing, in its own module so both the forum initializer and the
 * editor can use it without a circular import (the editor importing from the
 * entry module left `renderZones` undefined mid-cycle and stalled the editor).
 */

/**
 * Insert the widget drop-zone mount points so widgets can live anywhere: above
 * the discussion list, below it, and in the sidebar. Idempotent.
 */
/**
 * Force `doc.layout.zones` to be a plain OBJECT. Docs seeded server-side (or
 * round-tripped through PHP's json_decode) arrive with empty zones as a JS
 * ARRAY ([]); setting named keys on an array works in-session but
 * JSON.stringify silently drops them on save/publish — the vanishing-widgets
 * bug. Object.assign also rescues any named props already stranded on an
 * array from an earlier session.
 */
export function normalizeZones(doc) {
  if (!doc) return;
  const layout = doc.layout = doc.layout || {};
  if (!layout.zones || Array.isArray(layout.zones)) {
    layout.zones = Object.assign({}, layout.zones || {});
  }
  // Each widget's settings have the same hazard: a widget saved with no
  // settings came back as [], the inspector's named field (a Navigation title)
  // was set on the array, and publishing dropped it.
  Object.values(layout.zones).forEach(list => (Array.isArray(list) ? list : []).forEach(w => {
    if (w && (!w.settings || Array.isArray(w.settings))) w.settings = Object.assign({}, w.settings || {});
  }));
  // The per-extension settings bucket has the same []/{}. degradation hazard as
  // zones (PHP round-trips empty maps as arrays) — normalize it the same way so
  // ext panels can set doc.ext['<id>'] keys that survive stringify.
  const d = doc;
  if (!d.ext || Array.isArray(d.ext)) {
    d.ext = Object.assign({}, d.ext || {});
  }
}
export function ensureZones() {
  // Only the index gets widget zones — never the discussion view (where the
  // post scrubber would hide/overlap them).
  const index = document.querySelector('.IndexPage');
  if (!index) return;
  const make = name => {
    const el = document.createElement('div');
    el.setAttribute('data-bespoke-zone', name);
    el.className = 'Bespoke-zone';
    return el;
  };

  // Anchor the above/below zones to the main content list — which is the flat
  // .DiscussionList in feed/grid mode, but the .BespokeCategories index in
  // category mode (where we swap the list out). Without this fallback the
  // above/below widgets silently vanish in category mode. The toolbar is a
  // last-resort anchor so the zones still appear if the list is mid-load.
  const list = index.querySelector('.DiscussionList') || index.querySelector('.BespokeCategories') || index.querySelector('.IndexPage-toolbar');
  if (list && list.parentElement) {
    if (!index.querySelector('[data-bespoke-zone="above-list"]')) {
      // After the toolbar when that's our anchor; otherwise directly above the list.
      const ref = list.classList.contains('IndexPage-toolbar') ? list.nextSibling : list;
      list.parentElement.insertBefore(make('above-list'), ref);
    }
    if (!index.querySelector('[data-bespoke-zone="below-list"]')) {
      list.parentElement.appendChild(make('below-list'));
    }
  }

  // "Top of page": the first thing in the content column, above the nav and
  // the New Discussion button.
  const content = index.querySelector('.Page-content');
  if (content && content.firstElementChild?.getAttribute('data-bespoke-zone') !== 'top') {
    content.insertBefore(index.querySelector('[data-bespoke-zone="top"]') || make('top'), content.firstChild);
  }

  // "Header" and "Footer": full-width bands between the hero and the content,
  // and under it. Widgets have offered these zones all along, but nothing ever
  // created them, so a widget allowed only there had nowhere to go.
  const container = index.querySelector('.Page-main > .Page-container');
  if (container && container.parentElement) {
    if (!index.querySelector('[data-bespoke-zone="header"]')) {
      const band = make('header');
      band.classList.add('Bespoke-zone--band', 'container');
      container.parentElement.insertBefore(band, container);
    }
    if (!index.querySelector('[data-bespoke-zone="footer"]')) {
      const band = make('footer');
      band.classList.add('Bespoke-zone--band', 'container');
      container.parentElement.insertBefore(band, container.nextSibling);
    }
  }

  // Widget "sidebar" zone. On desktop it lives in the index's sidebar column
  // (the nav is a top pill bar via BespokeHeaderNav, so this column is widgets-
  // only). On mobile the pill bar is hidden and Flarum's own dropdown nav sits
  // in that column — so we move the widget zone BELOW the discussion list
  // (appended to the content column, after below-list) rather than letting it
  // push the list down. Idempotent and self-rehoming if the viewport crosses the
  // breakpoint on a later redraw.
  const side = index.querySelector('.Page-sidebar');
  const mobile = !!(window.matchMedia && window.matchMedia('(max-width: 767px)').matches);
  const sidebarHost = index.querySelector('[data-bespoke-zone="sidebar"]') || make('sidebar');
  const desired = mobile ? list && list.parentElement || side : side;
  if (desired && sidebarHost.parentElement !== desired) {
    desired.appendChild(sidebarHost);
  }
}

/**
 * A widget's "Show on" choice: every list page (default), the home page only,
 * or tag pages only. Every widget shows while editing, so it can be selected
 * and changed (and isn't lost: the editor saves what is on the page).
 */
function showsHere(w) {
  const showOn = w && w.settings && w.settings.showOn || 'all';
  if (showOn === 'all' || document.body.classList.contains('bespoke-editing')) return true;
  const tagPage = !!m.route.param('tags');
  if (showOn === 'tags') return tagPage;

  // Home: the forum's front page, whether that is / or the default route (/all).
  const path = (m.route.get() || '/').split(/[?#]/)[0];
  return !tagPage && (path === '/' || path === String(app.forum.attribute('defaultRoute') || '/'));
}

/**
 * Whether the active layout places a widget of this type in a zone.
 *
 * Placed anywhere, whatever its "Show on": placing the widget takes that piece
 * out of the built-in bar for good, so "Show on" decides where it appears and
 * nowhere else gets the bar's copy back. Wanted on every page? Show on every
 * list page, or place one per page type.
 */
export function isPlaced(type) {
  const doc = app.bespoke?.document || app.forum?.attribute('bespokePersonal') || app.forum?.attribute('bespoke.published');
  const zones = doc && doc.layout && doc.layout.zones || {};
  return Object.values(zones).some(list => Array.isArray(list) && list.some(w => w && w.type === type));
}

/** Remove the widget zones (and their widgets) — called when leaving the index. */
export function cleanupZones() {
  document.querySelectorAll('[data-bespoke-zone]').forEach(z => z.remove());
}

/**
 * Render every widget zone present in the DOM from the given layout. Iterating
 * the DOM (not just the doc) means emptied zones get cleared too. Each placed
 * widget is clickable to select it for editing while the editor is open.
 */
export function mountZones(registry, doc) {
  const zones = doc.layout && doc.layout.zones || {};
  // Desktop-only widgets (the nav, New Discussion) are left out on phones, as
  // the built-in bar is: Flarum's own menu and compose button take over there.
  // Not hidden with CSS — an empty widget box would still hold its zone open.
  // 🚨 Never while editing: the editor saves the layout by reading the widgets
  // on the page, so one left out here would be deleted on save.
  const phone = !document.body.classList.contains('bespoke-editing') && !!(window.matchMedia && window.matchMedia('(max-width: 767px)').matches);
  document.querySelectorAll('[data-bespoke-zone]').forEach(host => {
    const name = host.getAttribute('data-bespoke-zone');
    const widgets = zones[name] || [];
    const vnodes = widgets.map(w => {
      const def = registry.get(w.type);
      if (!def || phone && def.desktopOnly || !showsHere(w)) return null;
      return m('div.Bespoke-widget', {
        key: w.id,
        'data-bespoke-wid': w.id,
        onclick: e => {
          const select = app.bespoke.selectWidget;
          if (select) {
            e.preventDefault();
            e.stopPropagation();
            select(name, w.id);
          }
        }
      }, m(def.component, {
        settings: registry.resolve(w)
      }));
    }).filter(Boolean);
    m.render(host, vnodes);
  });
}

/**
 * Ensure the zones exist and (re-)render the active layout's widgets into them.
 * Uses app.bespoke.document (published on the forum, the draft while editing).
 * Safe to call on every redraw — Mithril wipes our appended zones when it
 * re-renders the page, so we rebuild them here to keep widgets on screen.
 */
export function renderZones(doc) {
  try {
    ensureZones();
    const layout = doc ?? app.bespoke.document;
    if (layout) mountZones(app.bespoke.widgets, layout);
  } catch (e) {
    console.error('[bespoke] renderZones failed', e);
  }
}